/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    FormLayout,
    useGlobalLang,
    Text,
    StackView,
    TEXT_WEIGHT,
    NoLabelGroup,
    DataList,
    colors,
    STACK_DIRECTION,
    FieldLayout,
    CONTROL_SIZE,
    Button,
    STACK_GAP,
    useModal,
    SELECTION_MODE,
    RadiobuttonKit,
    useGlobalServices,
} from '@veeam-vspc/shared/components';
import { BackupServerBackupJobGuestFSIndexingMode } from '@veeam-vspc/models/rest';

import type { VspcLang } from 'configs/languages';

import { SpecifyFolderModal } from '../SpecifyFolderModal/SpecifyFolderModal';

import type { SettingsWindowProps } from '../interfaces/settings-window-props';

export const LinuxSettings: React.FC<SettingsWindowProps> = observer(({
    active,
    data,
    onChange,
}) => {
    const lang = useGlobalLang<VspcLang>();
    const { notificationService } = useGlobalServices();

    const defaults = [
        {
            label: '/cdrom',
            value: '/cdrom',
        },
        {
            label: '/dev',
            value: '/dev',
        },
        {
            label: '/media',
            value: '/media',
        },
        {
            label: '/tmp',
            value: '/tmp',
        },
        {
            label: '/mnt',
            value: '/mnt',
        },
        {
            label: '/proc',
            value: '/proc',
        },
        {
            label: '/lost+found',
            value: '/lost+found',
        },
    ];

    const [selected, setSelected] = useState<string>();

    const [indexAllValues, setIndexAllValues] = useState<Record<string, string>[]>(() => {
        if (
            data.linuxIndexing?.indexingList?.length &&
            data.linuxIndexing?.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept
        ) {
            return data.linuxIndexing.indexingList.map(x => ({ label: x, value: x }));
        }

        return defaults;
    });

    const [indexOnlyValues, setIndexOnlyValues] = useState<Record<string, string>[]>(() => {
        if (
            data.linuxIndexing?.indexingList?.length &&
            data.linuxIndexing?.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexOnly
        ) {
            return data.linuxIndexing.indexingList.map(x => ({ label: x, value: x }));
        }

        return [];
    });

    const [folderModal, folderModalActions] = useModal({
        render: ({ deactivate }) => (
            <SpecifyFolderModal
                onConfirm={(newData) => {
                    if (data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept) {
                        if (indexAllValues.find(x => x.value === newData)) {
                            notificationService.warning(lang.INDEXING_SETTINGS, lang.SPECIFIED_FOLDER_ALREADY_EXISTS);
                            return;
                        }

                        setIndexAllValues([...indexAllValues, { label: newData, value: newData }]);
                    }
                    if (data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexOnly) {
                        if (indexOnlyValues.find(x => x.value === newData)) {
                            notificationService.warning(lang.INDEXING_SETTINGS, lang.SPECIFIED_FOLDER_ALREADY_EXISTS);
                            return;
                        }

                        setIndexOnlyValues([...indexOnlyValues, { label: newData, value: newData }]);
                    }
                    deactivate();
                }}
                deactivate={deactivate}
            />
        ),
    });

    const handleRemove = (value: string) => {
        if (!value) {
            return;
        }

        if (data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept) {
            setIndexAllValues(indexAllValues.filter(x => x.value !== value));
        }
        if (data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexOnly) {
            setIndexOnlyValues(indexOnlyValues.filter(x => x.value !== value));
        }
    };

    const handleChange = value => onChange({ ...data, linuxIndexing: { ...data.linuxIndexing, guestFSIndexingMode: value } });

    useEffect(() => {
        let indexingList = [];

        if (data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept) {
            indexingList = indexAllValues.map(({ value }) => value);
        }

        if (data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexOnly) {
            indexingList = indexOnlyValues.map(({ value }) => value);
        }

        onChange({
            ...data,
            linuxIndexing: {
                ...data.linuxIndexing,
                indexingList,
            },
        });
    }, [indexAllValues, indexOnlyValues, data.linuxIndexing.guestFSIndexingMode]);

    if (!active) {
        return null;
    }

    return (
        <FormLayout>
            <Text weight={TEXT_WEIGHT.bold}>
                {`${lang.GUEST_FILE_SYSTEM_INDEXING_MODE}:`}
            </Text>

            <NoLabelGroup
                content={(
                    <RadiobuttonKit
                        value={BackupServerBackupJobGuestFSIndexingMode.Disable}
                        onChange={handleChange}
                        checked={data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.Disable}
                    >
                        {lang.DISABLE_INDEXING}
                    </RadiobuttonKit>
                )}
            />

            <NoLabelGroup
                content={(
                    <RadiobuttonKit
                        value={BackupServerBackupJobGuestFSIndexingMode.IndexAll}
                        onChange={handleChange}
                        checked={data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexAll}
                    >
                        {lang.INDEX_EVERYTHING}
                    </RadiobuttonKit>
                )}
            />

            <NoLabelGroup
                content={(
                    <RadiobuttonKit
                        value={BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept}
                        onChange={handleChange}
                        checked={data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept}
                    >
                        {`${lang.INDEX_EVERYTHING_EXCEPT}:`}
                    </RadiobuttonKit>
                )}
                subContent={(
                    <FormLayout
                        direction={STACK_DIRECTION.column}
                    >
                        <StackView
                            direction={STACK_DIRECTION.row}
                            gap={STACK_GAP.l}
                        >
                            <FieldLayout
                                size={CONTROL_SIZE.l}
                                style={{
                                    height: '120px',
                                    border: `1px solid ${
                                        data.linuxIndexing.guestFSIndexingMode !== BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept
                                            ? colors.G500
                                            : colors.B500
                                    }`,
                                }}
                            >
                                <DataList
                                    selectionMode={SELECTION_MODE.single}
                                    data={
                                        data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept
                                            ? indexAllValues
                                            : []
                                    }
                                    valueGetter={item => item ? item.value : null}
                                    textGetter={item => item ? item.label : null}
                                    selected={[selected]}
                                    onChange={value => setSelected(value[0])}
                                    disabled={data.linuxIndexing.guestFSIndexingMode !== BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept}
                                    heightMode={120}
                                />
                            </FieldLayout>

                            <StackView
                                direction={STACK_DIRECTION.column}
                                gap={STACK_GAP.s}
                            >
                                <Button
                                    onClick={folderModalActions.activate}
                                    disabled={data.linuxIndexing.guestFSIndexingMode !== BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept}
                                >
                                    {`${lang.ADD}...`}
                                </Button>

                                <Button
                                    onClick={() => handleRemove(selected)}
                                    disabled={data.linuxIndexing.guestFSIndexingMode !== BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept}
                                >
                                    {lang.REMOVE}
                                </Button>

                                <Button
                                    onClick={() => setIndexAllValues([...defaults])}
                                    disabled={data.linuxIndexing.guestFSIndexingMode !== BackupServerBackupJobGuestFSIndexingMode.IndexAllExcept}
                                >
                                    {lang.DEFAULT}
                                </Button>
                            </StackView>
                        </StackView>
                    </FormLayout>
                )}
            />

            <NoLabelGroup
                content={(
                    <RadiobuttonKit
                        value={BackupServerBackupJobGuestFSIndexingMode.IndexOnly}
                        checked={data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexOnly}
                        onChange={handleChange}
                    >
                        {`${lang.INDEX_ONLY_FOLLOWING_FOLDERS}:`}
                    </RadiobuttonKit>
                )}
                subContent={(
                    <FormLayout
                        direction={STACK_DIRECTION.column}
                    >
                        <StackView
                            direction={STACK_DIRECTION.row}
                            gap={STACK_GAP.l}
                        >
                            <FieldLayout
                                size={CONTROL_SIZE.l}
                                style={{
                                    height: '120px',
                                    border: `1px solid ${
                                        data.linuxIndexing.guestFSIndexingMode !== BackupServerBackupJobGuestFSIndexingMode.IndexOnly
                                            ? colors.G500
                                            : colors.B500
                                    }`,
                                }}
                            >
                                <DataList
                                    selectionMode={SELECTION_MODE.single}
                                    data={
                                        data.linuxIndexing.guestFSIndexingMode === BackupServerBackupJobGuestFSIndexingMode.IndexOnly
                                            ? indexOnlyValues
                                            : []
                                    }
                                    valueGetter={item => item ? item.value : null}
                                    textGetter={item => item ? item.label : null}
                                    selected={[selected]}
                                    onChange={value => setSelected(value[0])}
                                    disabled={data.linuxIndexing.guestFSIndexingMode !== BackupServerBackupJobGuestFSIndexingMode.IndexOnly}
                                    heightMode={120}
                                />
                            </FieldLayout>

                            <StackView
                                direction={STACK_DIRECTION.column}
                                gap={STACK_GAP.s}
                            >
                                <Button
                                    onClick={folderModalActions.activate}
                                    disabled={data.linuxIndexing.guestFSIndexingMode !== BackupServerBackupJobGuestFSIndexingMode.IndexOnly}
                                >
                                    {`${lang.ADD}...`}
                                </Button>

                                <Button
                                    onClick={() => handleRemove(selected)}
                                    disabled={data.linuxIndexing.guestFSIndexingMode !== BackupServerBackupJobGuestFSIndexingMode.IndexOnly}
                                >
                                    {lang.REMOVE}
                                </Button>
                            </StackView>
                        </StackView>
                    </FormLayout>
                )}
            />

            {folderModal}
        </FormLayout>
    );
});
